import React, { useState, useMemo, useEffect } from 'react';
import { SearchBar } from './components/SearchBar';
import { FilterPanel } from './components/FilterPanel';
import { SortControls } from './components/SortControls';
import { Map } from './components/Map';
import { BusinessListOverlay } from './components/BusinessListOverlay';
import { GlobeSplash } from './components/GlobeSplash';
import { Donations } from './components/Donations';
import  FaqPage  from './components/faq';
import  ContactsPage  from './components/Contactspage';
import AppsPage from './components/apps';
import { useXMRBusinesses, SortOption, SortDirection } from './hooks/useXMRBusinesses';
import { Business } from './types';
import { Plus, Key, Menu, X, Loader, Layers, Check } from 'lucide-react';
import { AddBusinessModal } from './components/AddBusinessModal';
import { OSMAuthModal } from './components/OSMAuthModal';
import { isAuthenticated } from './services/osmApi';

// Define tile layers and the style control here
const tileLayers = { dark: { id: 'dark', name: 'Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>' }, light: { id: 'light', name: 'Light', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>' }, satellite: { id: 'satellite', name: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles © Esri' } };
const MapStyleControl = ({ currentStyle, onStyleChange }: { currentStyle: string, onStyleChange: (styleId: string) => void }) => { const [isOpen, setIsOpen] = useState(false); return ( <div className="absolute bottom-4 right-4 z-30"><div className="relative"> <button onClick={() => setIsOpen(!isOpen)} className="p-2 bg-gray-900/90 backdrop-blur-md rounded-lg text-white hover:bg-orange-500/50 transition-all shadow-lg border border-gray-700" title="Change map style"><Layers className="w-5 h-5" /></button> {isOpen && (<div className="absolute bottom-full mb-2 right-0 w-48 bg-gray-900/95 backdrop-blur-md rounded-lg border border-gray-700 p-2 shadow-xl"><div className="space-y-1"> {Object.values(tileLayers).map(layer => (<button key={layer.id} onClick={() => { onStyleChange(layer.id); setIsOpen(false); }} className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between transition-colors ${ currentStyle === layer.id ? 'bg-orange-500/20 text-orange-300' : 'text-gray-300 hover:bg-gray-700/50' }`}><span>{layer.name}</span>{currentStyle === layer.id && <Check className="w-4 h-4 text-orange-400" />}</button>))} </div></div>)} </div></div> ); };


function App() {
  const [appView, setAppView] = useState('splash');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isAddBusinessOpen, setIsAddBusinessOpen] = useState(false);
  const [isOSMAuthOpen, setIsOSMAuthOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('country');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [mapStyle, setMapStyle] = useState('dark');
  const [viewport, setViewport] = useState({
    latitude: 40.7128, // Start at New York
    longitude: -74.0060,
    zoom: 10
  });
  const [preloadedBusinesses, setPreloadedBusinesses] = useState<Business[] | null>(null);

  const { businesses, loading, error, refreshBusinesses, xmrPrice } = useXMRBusinesses(preloadedBusinesses, viewport, globalSearch, sortBy, sortDirection);

  const filteredBusinesses = useMemo(() => {
    if (!Array.isArray(businesses)) return [];
    return businesses.filter(business => {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const matchesSearch = business.name.toLowerCase().includes(lowerSearchTerm) ||
                           (business.description && business.description.toLowerCase().includes(lowerSearchTerm)) ||
                           (business.address && business.address.toLowerCase().includes(lowerSearchTerm));
      
      const matchesCategory = selectedCategories.length === 0 || 
                             selectedCategories.includes(business.category);
      return matchesSearch && matchesCategory;
    });
  }, [businesses, searchTerm, selectedCategories]);

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const handleBusinessSelect = (business: Business) => {
    setSelectedBusiness(selectedBusiness?.id === business.id ? null : business);
  };

  const handleViewportChange = (newViewport: { latitude: number; longitude: number; zoom: number }) => {
    setViewport(newViewport);
  };

  const handleSortChange = (newSortBy: SortOption, newDirection: SortDirection) => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };
  
  const navItems = [
    { tab: 'home', label: 'Home' },
    { tab: 'apps', label: 'Apps' },
    { tab: 'map', label: 'Map' },
    { tab: 'donations', label: 'Donations' },
    { tab: 'contacts', label: 'Network' },
    { tab: 'faq', label: 'FAQ' },
  ];

  const handleNavClick = (tabName: string) => {
    if (tabName === 'home') {
        setAppView('splash');
    } else {
        setActiveTab(tabName);
        setAppView('main');
    }
    setIsMenuOpen(false);
  };

  const handleEnterMap = (businesses: Business[]) => {
    setPreloadedBusinesses(businesses);
    setIsTransitioning(true);
  };

  useEffect(() => {
    if (isTransitioning) {
        const timer = setTimeout(() => {
            setAppView('main');
            setActiveTab('map');
            setIsTransitioning(false);
        }, 100);
        return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  const renderContent = () => {
    // Wrap non-map content in a scrollable container
    const ContentWrapper: React.FC<{children: React.ReactNode}> = ({ children }) => (
      <div className="flex-grow overflow-y-auto p-4 md:p-6 max-w-7xl w-full mx-auto">
        {children}
      </div>
    );

    switch (activeTab) {
      case 'map':
        return (
          <main className="relative z-10 max-w-7xl w-full mx-auto p-4 md:p-6 flex-grow flex flex-col min-h-0">
            <div className="space-y-4 mb-4">
              <div className="relative">
                <SearchBar 
                  searchTerm={searchTerm} 
                  onSearchChange={setSearchTerm} 
                  onFilterToggle={() => {setIsFilterOpen(!isFilterOpen); if(!isFilterOpen) setIsSortOpen(false);}} 
                  isFilterOpen={isFilterOpen} 
                  onSortToggle={() => {setIsSortOpen(!isSortOpen); if(!isSortOpen) setIsFilterOpen(false);}} 
                  isSortOpen={isSortOpen} 
                />
                <FilterPanel 
                  selectedCategories={selectedCategories} 
                  onCategoryToggle={handleCategoryToggle} 
                  isOpen={isFilterOpen} 
                  onClose={() => setIsFilterOpen(false)} 
                />
              </div>
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isSortOpen ? 'max-h-screen' : 'max-h-0'}`}>
                <div className="pt-2">
                  <SortControls 
                    sortBy={sortBy} 
                    sortDirection={sortDirection} 
                    onSortChange={handleSortChange} 
                    globalSearch={globalSearch} 
                    onGlobalSearchToggle={setGlobalSearch} 
                    businessCount={filteredBusinesses.length} 
                  />
                </div>
              </div>
            </div>
            <div className="flex-grow flex flex-col min-h-0">
              <div className="flex-grow h-0 rounded-xl border border-gray-700 overflow-hidden shadow-2xl relative">
                <Map 
                  businesses={filteredBusinesses} 
                  selectedBusiness={selectedBusiness} 
                  onBusinessSelect={handleBusinessSelect} 
                  viewport={viewport} 
                  loading={loading} 
                  onViewportChange={handleViewportChange}
                  mapStyle={mapStyle}
                  onMapStyleChange={setMapStyle}
                />
                <BusinessListOverlay
                  businesses={filteredBusinesses}
                  selectedBusiness={selectedBusiness}
                  onBusinessSelect={handleBusinessSelect}
                  loading={loading}
                />
                <MapStyleControl currentStyle={mapStyle} onStyleChange={setMapStyle} />
              </div>
            </div>
          </main>
        );
      case 'donations':
        return <ContentWrapper><Donations /></ContentWrapper>;
      case 'apps' : 
        return <ContentWrapper><AppsPage setActiveTab={setActiveTab} /></ContentWrapper>;
      case 'contacts':
        return <ContentWrapper><ContactsPage /></ContentWrapper>;
      case 'faq': 
        return <ContentWrapper><FaqPage /></ContentWrapper>;
      default:
        return null;
    }
  };

  if (appView === 'splash') {
    return <GlobeSplash onEnter={handleEnterMap} />;
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex flex-col overflow-hidden">
      <div className="fixed inset-0 opacity-5 -z-10 bg-black">
        <div className="w-full h-full" style={{ backgroundImage: `radial-gradient(circle at 25% 25%, #ff6600 0%, transparent 50%), radial-gradient(circle at 75% 75%, #ff6600 0%, transparent 50%)` }} />
      </div>

      <header className="relative z-30 bg-gray-900/80 backdrop-blur-md border-b border-gray-800 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent hidden sm:block">
                  XMRmap
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
               {!isAuthenticated() && (
                <button 
                  onClick={() => setIsOSMAuthOpen(true)} 
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/40 text-green-300 rounded-md transition-colors text-sm"
                >
                  <Key className="w-4 h-4" /> 
                </button>
              )}
              <button 
                onClick={() => setIsAddBusinessOpen(true)} 
                className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 rounded-md transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
              </button>
              <div>
                <button onClick={() => setIsMenuOpen(true)} className="p-1">
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {isMenuOpen && (
        <div className="fixed inset-0 bg-gray-900 z-50 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">XMRmap</h1>
            </div>
            <button onClick={() => setIsMenuOpen(false)} className="p-2">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex flex-col items-center gap-8">
            {navItems.map(item => (
              <button
                key={item.tab}
                onClick={() => handleNavClick(item.tab)}
                className={`text-2xl font-semibold transition-colors ${
                  activeTab === item.tab ? 'text-orange-400' : 'text-gray-300 hover:text-orange-400'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="flex-grow flex flex-col min-h-0">
        {isTransitioning ? (
            <div className="flex-grow flex items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-orange-500" />
            </div>
        ) : renderContent()}
      </div>

      <footer className="relative z-10 border-t border-gray-800 p-4 bg-gray-900/80 backdrop-blur-md flex-shrink-0">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400 text-center md:text-left">Supporting financial privacy</p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-gray-500">
              <span>Data © OpenStreetMap contributors</span>
              <span className="hidden md:block">•</span>
              <span>Powered by Overpass API</span>
            </div>
        </div>
      </footer>
      
      <AddBusinessModal 
        isOpen={isAddBusinessOpen} 
        onClose={() => setIsAddBusinessOpen(false)} 
        onSubmit={()=>{}} 
        onOSMSubmit={()=>{}} 
      />
      <OSMAuthModal 
        isOpen={isOSMAuthOpen} 
        onClose={() => setIsOSMAuthOpen(false)} 
        onAuthenticated={()=>{}} 
      />
    </div>
  );
}

export default App;
